import type { JSX } from 'react';
import {
  type ApiKeyResource,
  type EnvironmentValue,
  environmentSchema,
} from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { EnvironmentBadge, ENVIRONMENT_LABELS } from './EnvironmentBadge';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardHead } from '@/components/ui/card';
import { CodeBlock } from '@/components/ui/code';
import { NativeSelect } from '@/components/ui/native-select';
import { Tag } from '@/components/ui/tag';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';

interface CreatedKey {
  id: string;
  kind: string;
  environment: EnvironmentValue;
  prefix: string;
  key: string;
}

type KeyFilter = 'active' | 'all';

const COLUMN_COUNT = 5;

/**
 * API keys of a client: creation (shown once) in the chosen environment,
 * revocation, revoked keys on demand. Staging keys serve the client's staging
 * ERP and are never billed (D-ENV.1).
 */
export function ClientApiKeys({ clientId }: { clientId: string }): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const base = `/internal/clients/${clientId}`;
  const [newKey, setNewKey] = useState<CreatedKey | null>(null);
  const [filter, setFilter] = useState<KeyFilter>('active');
  const [environment, setEnvironment] = useState<EnvironmentValue>('production');

  const keys = useQuery({
    queryKey: ['keys', clientId],
    queryFn: () => internalFetch<{ keys: ApiKeyResource[] }>(`${base}/keys`),
  });

  const createKey = useMutation({
    mutationFn: (kind: 'service' | 'admin') =>
      internalSend<CreatedKey>('POST', `${base}/keys`, { kind, environment }),
    onSuccess: (created) => {
      setNewKey(created);
      void qc.invalidateQueries({ queryKey: ['keys', clientId] });
    },
  });
  const revokeKey = useMutation({
    mutationFn: (keyId: string) => internalSend('DELETE', `${base}/keys/${keyId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['keys', clientId] });
      notify('Clé révoquée');
    },
  });

  const allKeys = keys.data?.keys ?? [];
  const activeKeys = allKeys.filter((key) => key.revokedAt === null);
  const revokedKeys = allKeys.filter((key) => key.revokedAt !== null);
  const showRevoked = filter === 'all' && revokedKeys.length > 0;
  const visibleKeys = showRevoked ? [...activeKeys, ...revokedKeys] : activeKeys;

  return (
    <Card>
      <CardHead
        title="Clés API"
        description="Clés de service (requêtes de l'ERP) et d'administration. Une clé Staging sert l'ERP de recette : ses requêtes ne sont pas facturées."
        className="pb-4"
        actions={
          <>
            <NativeSelect
              controlSize="sm"
              wrapperClassName="w-32"
              aria-label="Environnement de la nouvelle clé"
              value={environment}
              onChange={(event) => {
                const parsed = environmentSchema.safeParse(event.target.value);
                if (parsed.success) {
                  setEnvironment(parsed.data);
                }
              }}
            >
              {environmentSchema.options.map((value) => (
                <option key={value} value={value}>
                  {ENVIRONMENT_LABELS[value]}
                </option>
              ))}
            </NativeSelect>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                createKey.mutate('service');
              }}
            >
              <Plus aria-hidden="true" />
              Nouvelle clé de service
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                createKey.mutate('admin');
              }}
            >
              Nouvelle clé d&apos;administration
            </Button>
          </>
        }
      />
      {newKey !== null ? (
        <div className="px-4 pb-4">
          <Callout
            variant="success"
            title={`Clé ${ENVIRONMENT_LABELS[newKey.environment]} créée (affichée une seule fois)`}
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewKey(null);
                }}
              >
                Fermer
              </Button>
            }
          >
            <CodeBlock className="mt-2">{newKey.key}</CodeBlock>
          </Callout>
        </div>
      ) : null}
      {revokedKeys.length > 0 ? (
        <CardBar
          className="border-t"
          start={
            <TableTabs<KeyFilter>
              aria-label="Filtrer les clés"
              value={filter}
              onValueChange={setFilter}
              items={[
                { value: 'active', label: 'Actives', count: activeKeys.length },
                { value: 'all', label: 'Toutes, révoquées comprises', count: allKeys.length },
              ]}
            />
          }
        />
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Environnement</TableHead>
            <TableHead>Préfixe</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleKeys.map((key) => (
            <TableRow key={key.id}>
              <TableCell>
                <Tag mono>{key.kind}</Tag>
              </TableCell>
              <TableCell>
                <EnvironmentBadge environment={key.environment} />
              </TableCell>
              <TableCell kind="mono">{key.prefix}…</TableCell>
              <TableCell>
                {key.revokedAt === null ? (
                  <Badge variant="success">Active</Badge>
                ) : (
                  <Badge variant="neutral">Révoquée</Badge>
                )}
              </TableCell>
              <TableCell kind="act" className="w-auto">
                {key.revokedAt === null ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    className="text-danger-fg"
                    aria-label={`Révoquer la clé ${key.prefix}`}
                    onClick={() => {
                      revokeKey.mutate(key.id);
                    }}
                  >
                    Révoquer
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
          {keys.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
          ) : null}
          {visibleKeys.length === 0 && !keys.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>
              {showRevoked ? 'Aucune clé.' : 'Aucune clé active.'}
            </TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
    </Card>
  );
}
